import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";

const LINES = [
  { key: "feel", label: "Feelings", color: "#4A5D4E" },
  { key: "past", label: "The Past", color: "#B37A5F" },
  { key: "future", label: "The Future", color: "#6B705C" },
  { key: "inner_self_talk", label: "Inner Self Talk", color: "#9C6A53" },
];

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function QolProbabilitiesChart({ userId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const endpoint = userId
          ? `/users/${userId}/qol-probabilities`
          : "/qol/probabilities";
        const { data: rows } = await api.get(endpoint);
        setData(rows);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  // Fixed-height chart; horizontal layout with dates on the X-axis
  const height = 420;

  return (
    <section
      className="mt-12 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
      data-testid="qol-probabilities-chart"
    >
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp strokeWidth={1.5} className="text-[#B37A5F]" size={18} />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
              Probability of a good day · over time
            </p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#1A1D1A] tracking-tight font-semibold">
            Your trend through time
          </h2>
          <p className="text-sm text-[#5C635A] mt-2 max-w-2xl leading-relaxed">
            Each line shows the probability that today is a good day for one of the four
            subjects. The starting point is set by your onboarding (more <span className="text-[#4A5D4E] font-semibold">perfect</span>{" "}
            and <span className="text-[#4A5D4E] font-semibold">very good</span> days lift it; more{" "}
            <span className="text-[#9C6A53] font-semibold">bad</span> and <span className="text-[#9C6A53] font-semibold">very bad</span> days lower it). After that, every day you mark a subject as <span className="text-[#4A5D4E] font-semibold">good</span> it nudges up by 0.1%; every day marked <span className="text-[#9C6A53] font-semibold">bad</span> nudges it down by 0.1%. Neutral days hold steady.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold mt-6">Loading…</p>
      ) : data.length <= 1 ? (
        <div
          className="mt-6 bg-[#F2F0EA] border border-[#E5E2DC] rounded-xl p-8 text-center text-[#5C635A]"
          data-testid="qol-chart-empty"
        >
          Your trend will appear here starting tomorrow — come back after you've held a toggle for at
          least one full day.
        </div>
      ) : (
        <div className="mt-8" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 24, left: 8, bottom: 10 }}
            >
              <CartesianGrid stroke="#E5E2DC" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#8A9188"
                tick={{ fontSize: 11, fill: "#5C635A" }}
                axisLine={{ stroke: "#E5E2DC" }}
                tickLine={{ stroke: "#E5E2DC" }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                type="number"
                domain={[1, 99]}
                ticks={[1, 25, 50, 75, 99]}
                tickFormatter={(v) => `${v}%`}
                stroke="#8A9188"
                tick={{ fontSize: 11, fill: "#5C635A" }}
                axisLine={{ stroke: "#E5E2DC" }}
                tickLine={{ stroke: "#E5E2DC" }}
                width={48}
              />
              <Tooltip
                contentStyle={{
                  background: "#FFFFFF",
                  border: "1px solid #E5E2DC",
                  borderRadius: "12px",
                  fontSize: "12px",
                  color: "#1A1D1A",
                }}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
                formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ paddingBottom: 18, fontSize: 12, color: "#5C635A" }}
                iconType="circle"
              />
              {LINES.map(({ key, label, color }) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={label}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: color, strokeWidth: 2, fill: "#fff" }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
